import { useMemo, useState } from "react";
import { PRODUCTS } from "@/data/catalog";
import { CHARACTERS, NEEDS, characterResult } from "@/data/characters";
import { useLang } from "@/i18n/LanguageContext";
import { useCart } from "@/context/CartContext";
import { QuizResult } from "@/components/QuizResult";

const field = { marginTop: 8, width: "100%", maxWidth: 320, background: "transparent", border: 0, borderBottom: "1px solid rgba(214,210,199,.4)", padding: "8px 0", fontSize: 28, color: "var(--cream)", fontFamily: "Cormorant Garamond, serif", outline: "none" };

export const PrescriptionQuiz = ({ onDone }) => {
  const [step, setStep] = useState(0);
  const [name, setName] = useState("");
  const [weight, setWeight] = useState("");
  const [unit, setUnit] = useState("lb");
  const [age, setAge] = useState("");
  const [characters, setCharacters] = useState([]);
  const [need, setNeed] = useState(null);
  const { t } = useLang();
  const { setWeightLb } = useCart();
  const L = (pair) => t(pair[0], pair[1]);
  const weightLb = useMemo(() => { const w = parseFloat(weight); return w ? (unit === "kg" ? w * 2.20462 : w) : 0; }, [weight, unit]);
  const picks = characterResult({ characters, ageYears: age, need }).map((id) => PRODUCTS.find((p) => p.id === id));
  const canNext = [characters.length > 0, name.trim(), weightLb > 0, age !== "", need][step];
  const next = () => { if (step === 4) { setWeightLb(weightLb); onDone?.(true); } setStep((s) => s + 1); };
  const restart = () => { setStep(0); setNeed(null); setCharacters([]); onDone?.(false); };
  const toggle = (id) => setCharacters((cs) => (cs.includes(id) ? cs.filter((c) => c !== id) : [...cs, id]));

  if (step === 5) {
    return <QuizResult picks={picks} name={name.trim()} weightLb={Math.round(weightLb)} onRestart={restart} characters={characters}
      dog={{ name: name.trim(), characters, need, weight_lb: Math.min(250, Math.max(1, Math.round(weightLb))), age_years: age === "" ? null : Math.min(25, Number(age)) }} />;
  }
  const Q = [t("What sort of dog are we dealing with?", "À quel genre de chien avons-nous affaire?"), t("First, the dog's name.", "D'abord, le nom du chien."), t("And their weight?", "Et son poids?"), t("How many years behind them?", "Combien d'années derrière lui?"), t("Lastly, what are we working on?", "Enfin, sur quoi travaillons-nous?")][step];
  const SUB = [t("Tick any that ring true. We shan't tell the others.", "Cochez tout ce qui sonne juste. Nous n'en dirons rien aux autres."), "", t("We use it to work out the daily measure.", "Il sert à calculer la mesure quotidienne."), t("Every gray hair counts in their favour here.", "Chaque poil gris compte en sa faveur ici."), ""][step];
  return (
    <div data-testid="quiz-card" data-no-translate>
      <div className="eyebrow" data-testid="quiz-step-label">{t(`Find your jar · Question ${step + 1} of 5`, `Trouvez son pot · Question ${step + 1} sur 5`)}</div>
      <div className="progress" data-testid="quiz-progress" aria-hidden="true">{[0, 1, 2, 3, 4].map((i) => <i key={i} className={i <= step ? "on" : ""} />)}</div>
      <h1>{Q}</h1>
      {SUB && <p className="sub">{SUB}</p>}
      {step === 0 && (
        <div className="choices">
          {CHARACTERS.map((c) => (
            <button type="button" key={c.id} className={`choice${characters.includes(c.id) ? " on" : ""}`} aria-pressed={characters.includes(c.id)} onClick={() => toggle(c.id)} data-testid={`quiz-character-${c.id}`} style={{ textAlign: "left" }}>
              <b>{L(c.title)}</b><span>{L(c.line)}</span>
            </button>
          ))}
        </div>
      )}
      {step === 1 && <input aria-label="Dog's name" data-testid="quiz-name-input" style={field} value={name} onChange={(e) => setName(e.target.value.slice(0, 18))} placeholder={t("Biscuit, perhaps", "Biscuit, peut-être")} />}
      {step === 2 && (
        <div style={{ display: "flex", alignItems: "flex-end", gap: 12 }}>
          <input aria-label="Weight" data-testid="quiz-weight-input" inputMode="decimal" style={{ ...field, width: 120 }} value={weight} onChange={(e) => setWeight(e.target.value.replace(/[^0-9.]/g, ""))} placeholder="45" />
          {["lb", "kg"].map((u) => <button type="button" key={u} className={`choice${unit === u ? " on" : ""}`} style={{ padding: "8px 14px" }} onClick={() => setUnit(u)} data-testid={`quiz-unit-${u}`}>{u.toUpperCase()}</button>)}
        </div>
      )}
      {step === 3 && <input aria-label="Age in years" data-testid="quiz-age-input" inputMode="numeric" style={{ ...field, width: 120 }} value={age} onChange={(e) => setAge(e.target.value.replace(/[^0-9]/g, ""))} placeholder="7" />}
      {step === 4 && (
        <div className="choices">
          {NEEDS.map((n) => <button type="button" key={n.id} className={`choice${need === n.id ? " on" : ""}`} onClick={() => setNeed(n.id)} data-testid={`quiz-need-${n.id}`} style={{ textAlign: "left" }}><b>{L(n.label)}</b></button>)}
        </div>
      )}
      <div className="navbtns">
        <button type="button" className="btn-text" style={{ color: "var(--muted)", borderColor: "transparent", visibility: step ? "visible" : "hidden" }} onClick={() => setStep((s) => s - 1)} data-testid="quiz-back">{t("Back", "Retour")}</button>
        <button type="button" className="btn btn-primary" disabled={!canNext} style={{ opacity: canNext ? 1 : 0.45 }} onClick={next} data-testid={step === 4 ? "quiz-finish" : "quiz-next"}>{step === 4 ? t("See their jar", "Voir son pot") : t("Continue", "Continuer")}</button>
      </div>
    </div>
  );
};
